import { useEffect, useState } from 'react';
import { trpc } from '@/lib/trpc';
import { Modal } from '@/components/ui/Modal';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';

interface PasseioOption {
  id: string;
  nome: string;
  limite: number;
  inscritos: number;
}

interface AddInscricaoModalProps {
  isOpen: boolean;
  onClose: () => void;
  passeios: PasseioOption[];
  passeioIdInicial?: string;
  onSuccess: () => void;
}

const selectClass =
  'w-full rounded-lg border border-gray-300 px-3 py-2 text-gray-900 transition-colors focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500';

// Máscara de CPF para exibição (o servidor remove a máscara ao validar).
const formatCpf = (value: string): string => {
  const d = value.replace(/\D/g, '').slice(0, 11);
  return d
    .replace(/^(\d{3})(\d)/, '$1.$2')
    .replace(/^(\d{3})\.(\d{3})(\d)/, '$1.$2.$3')
    .replace(/\.(\d{3})(\d)/, '.$1-$2');
};

export function AddInscricaoModal({
  isOpen,
  onClose,
  passeios,
  passeioIdInicial,
  onSuccess,
}: AddInscricaoModalProps) {
  const [passeioId, setPasseioId] = useState('');
  const [nomeCompleto, setNomeCompleto] = useState('');
  const [clubeId, setClubeId] = useState('');
  const [cpf, setCpf] = useState('');

  const { data: clubes } = trpc.passeios.listarClubes.useQuery(undefined, {
    enabled: isOpen,
  });

  const inscrever = trpc.passeios.inscrever.useMutation({
    onSuccess: () => {
      onSuccess();
      onClose();
    },
  });

  // Reseta o formulário sempre que o modal abre, pré-selecionando o passeio atual.
  useEffect(() => {
    if (isOpen) {
      setPasseioId(passeioIdInicial ?? passeios[0]?.id ?? '');
      setNomeCompleto('');
      setClubeId('');
      setCpf('');
      inscrever.reset();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, passeioIdInicial]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    inscrever.mutate({ passeioId, clubeId, nomeCompleto, cpf });
  };

  const passeioSelecionado = passeios.find((p) => p.id === passeioId);
  const esgotado = passeioSelecionado
    ? passeioSelecionado.inscritos >= passeioSelecionado.limite
    : false;

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Nova inscrição" size="md">
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label htmlFor="passeio" className="mb-1 block text-sm font-medium text-gray-700">
            Passeio
          </label>
          <select
            id="passeio"
            value={passeioId}
            onChange={(e) => setPasseioId(e.target.value)}
            className={selectClass}
            required
          >
            {passeios.map((p) => {
              const cheio = p.inscritos >= p.limite;
              return (
                <option key={p.id} value={p.id} disabled={cheio}>
                  {p.nome} ({p.inscritos}/{p.limite}){cheio ? ' — esgotado' : ''}
                </option>
              );
            })}
          </select>
          {passeioSelecionado && !esgotado && (
            <p className="mt-1 text-xs text-gray-500">
              {passeioSelecionado.limite - passeioSelecionado.inscritos} vaga(s) restante(s)
            </p>
          )}
        </div>

        <Input
          id="nomeCompleto"
          label="Nome completo"
          value={nomeCompleto}
          onChange={(e) => setNomeCompleto(e.target.value)}
          placeholder="Nome e sobrenome"
          autoComplete="off"
          required
        />

        <div>
          <label htmlFor="clube" className="mb-1 block text-sm font-medium text-gray-700">
            Clube
          </label>
          <select
            id="clube"
            value={clubeId}
            onChange={(e) => setClubeId(e.target.value)}
            className={selectClass}
            required
          >
            <option value="" disabled>
              Selecione o clube
            </option>
            {clubes?.map((c) => (
              <option key={c.id} value={c.id}>
                {c.nome}
              </option>
            ))}
          </select>
        </div>

        <Input
          id="cpf"
          label="CPF"
          value={formatCpf(cpf)}
          onChange={(e) => setCpf(e.target.value.replace(/\D/g, '').slice(0, 11))}
          placeholder="000.000.000-00"
          inputMode="numeric"
          autoComplete="off"
          required
        />

        {inscrever.error && (
          <p className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-600">
            {inscrever.error.message}
          </p>
        )}

        <div className="flex justify-end gap-2 pt-2">
          <Button type="button" variant="outline" onClick={onClose} disabled={inscrever.isPending}>
            Cancelar
          </Button>
          <Button type="submit" disabled={inscrever.isPending || esgotado}>
            {inscrever.isPending ? 'Salvando…' : 'Adicionar'}
          </Button>
        </div>
      </form>
    </Modal>
  );
}
